import { defineStore } from 'pinia'
import { ref } from 'vue'
import {
  doc,
  collection,
  query,
  where,
  onSnapshot,
  setDoc,
  getDocs,
  type Unsubscribe
} from 'firebase/firestore'
import { db } from '../firebase'

export interface SavedRecipeProgress {
  timestamp: number
  completedSteps: Record<string, number[]> // recipeId -> array of completed step indices
  checkedIngredients: Record<string, string[]> // recipeId -> array of checked articleIds or keys
}

const TWELVE_HOURS_MS = 12 * 60 * 60 * 1000

export const isRecipeProgressEmpty = (p: SavedRecipeProgress | null | undefined): boolean => {
  if (!p) return true
  const stepsCount = Object.values(p.completedSteps || {}).reduce((acc, arr) => acc + (arr?.length || 0), 0)
  const ingsCount = Object.values(p.checkedIngredients || {}).reduce((acc, arr) => acc + (arr?.length || 0), 0)
  return stepsCount === 0 && ingsCount === 0
}

export const useRecipeProgressStore = defineStore('recipeProgress', () => {
  const loadRecipeProgress = (): SavedRecipeProgress => {
    try {
      const raw = typeof localStorage !== 'undefined' ? localStorage.getItem('recipe_progress_state_v1') : null
      if (!raw) return { timestamp: 0, completedSteps: {}, checkedIngredients: {} }
      const parsed: SavedRecipeProgress = JSON.parse(raw)
      if (!parsed.timestamp || Date.now() - parsed.timestamp > TWELVE_HOURS_MS) {
        if (typeof localStorage !== 'undefined') localStorage.removeItem('recipe_progress_state_v1')
        return { timestamp: 0, completedSteps: {}, checkedIngredients: {} }
      }
      return {
        timestamp: parsed.timestamp,
        completedSteps: parsed.completedSteps || {},
        checkedIngredients: parsed.checkedIngredients || {}
      }
    } catch {
      return { timestamp: 0, completedSteps: {}, checkedIngredients: {} }
    }
  }

  const recipeProgress = ref<SavedRecipeProgress>(loadRecipeProgress())
  const currentSyncUid = ref<string | null>(null)
  const pairedWatchTokens = ref<string[]>([])
  const unsubs: Unsubscribe[] = []

  const checkRecipeProgressExpiration = () => {
    if (recipeProgress.value.timestamp > 0 && Date.now() - recipeProgress.value.timestamp > TWELVE_HOURS_MS) {
      recipeProgress.value = { timestamp: 0, completedSteps: {}, checkedIngredients: {} }
      if (typeof localStorage !== 'undefined') localStorage.removeItem('recipe_progress_state_v1')
    }
  }

  const persistToLocalStorage = () => {
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('recipe_progress_state_v1', JSON.stringify(recipeProgress.value))
      }
    } catch (e) {
      console.warn('Failed to save recipe progress:', e)
    }
  }

  const syncToFirestore = async () => {
    const uid = currentSyncUid.value
    if (!uid || !db) return

    const payload = {
      timestamp: recipeProgress.value.timestamp,
      completedSteps: recipeProgress.value.completedSteps,
      checkedIngredients: recipeProgress.value.checkedIngredients
    }

    try {
      // 1. Sync to user_data document
      await setDoc(doc(db, 'user_data', uid), { recipeProgress: payload }, { merge: true }).catch(() => {})

      // 2. Sync to all paired watch devices
      if (pairedWatchTokens.value.length > 0) {
        const updatePromises = pairedWatchTokens.value.map(token =>
          setDoc(doc(db, 'watch_devices', token), {
            recipeProgress: payload,
            lastSeen: Date.now()
          }, { merge: true }).catch(() => {})
        )
        await Promise.all(updatePromises)
      } else {
        // Fallback: query any watch_devices for this owner
        try {
          const snap = await getDocs(query(collection(db, 'watch_devices'), where('ownerId', '==', uid)))
          if (!snap.empty) {
            pairedWatchTokens.value = snap.docs.map(d => d.id)
            const updates = snap.docs.map(d =>
              setDoc(d.ref, {
                recipeProgress: payload,
                lastSeen: Date.now()
              }, { merge: true }).catch(() => {})
            )
            await Promise.all(updates)
          }
        } catch {}
      }
    } catch (err) {
      console.warn('[recipeProgressStore] Failed to sync progress to Firestore:', err)
    }
  }

  const applyRemoteProgress = (remote: any) => {
    if (!remote || typeof remote !== 'object') return
    const remoteTimestamp = Number(remote.timestamp) || 0
    if (!remoteTimestamp) return

    // Check expiration: ignore remote if older than 12h
    if (Date.now() - remoteTimestamp > TWELVE_HOURS_MS) {
      return
    }

    const localTimestamp = Number(recipeProgress.value.timestamp) || 0
    const localEmpty = isRecipeProgressEmpty(recipeProgress.value)
    const remoteEmpty = isRecipeProgressEmpty(remote)

    // Accept remote if local has no progress, or if remote is strictly newer
    const shouldApply = (localEmpty && !remoteEmpty) || (remoteTimestamp > localTimestamp)

    if (shouldApply) {
      const completedSteps: Record<string, number[]> = {}
      if (remote.completedSteps && typeof remote.completedSteps === 'object') {
        for (const [k, v] of Object.entries(remote.completedSteps)) {
          if (Array.isArray(v)) {
            completedSteps[k] = v.map(n => Number(n)).filter(n => !isNaN(n))
          }
        }
      }

      const checkedIngredients: Record<string, string[]> = {}
      if (remote.checkedIngredients && typeof remote.checkedIngredients === 'object') {
        for (const [k, v] of Object.entries(remote.checkedIngredients)) {
          if (Array.isArray(v)) {
            checkedIngredients[k] = v.map(s => String(s)).filter(Boolean)
          }
        }
      }

      recipeProgress.value = {
        timestamp: remoteTimestamp,
        completedSteps,
        checkedIngredients
      }
      persistToLocalStorage()
    }
  }

  const initSync = (uid: string) => {
    stopSync()
    if (!uid || !db) return
    currentSyncUid.value = uid

    try {
      // Listen to watch_devices for this user
      const qWatch = query(collection(db, 'watch_devices'), where('ownerId', '==', uid))
      const unsubWatch = onSnapshot(qWatch, (snapshot) => {
        const tokens: string[] = []
        snapshot.forEach(docSnap => {
          tokens.push(docSnap.id)
          const data = docSnap.data()
          if (data && data.recipeProgress) {
            applyRemoteProgress(data.recipeProgress)
          }
        })
        pairedWatchTokens.value = tokens
      }, (err) => {
        console.warn('[recipeProgressStore] Watch devices listener warning:', err)
      })
      unsubs.push(unsubWatch)

      // Listen to user_data for this user
      const userRef = doc(db, 'user_data', uid)
      const unsubUser = onSnapshot(userRef, (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data()
          if (data && data.recipeProgress) {
            applyRemoteProgress(data.recipeProgress)
          }
        }
      }, (err) => {
        console.warn('[recipeProgressStore] user_data listener warning:', err)
      })
      unsubs.push(unsubUser)
    } catch (err) {
      console.warn('[recipeProgressStore] Failed to attach sync listeners:', err)
    }
  }

  const stopSync = () => {
    while (unsubs.length > 0) {
      const unsub = unsubs.pop()
      if (unsub) {
        try { unsub() } catch {}
      }
    }
    currentSyncUid.value = null
    pairedWatchTokens.value = []
  }

  const saveRecipeProgress = () => {
    checkRecipeProgressExpiration()
    recipeProgress.value.timestamp = Date.now()
    persistToLocalStorage()
    syncToFirestore()
  }

  const isStepCompleted = (recipeId: string, stepIndex: number): boolean => {
    checkRecipeProgressExpiration()
    const steps = recipeProgress.value.completedSteps[recipeId]
    return steps ? steps.includes(stepIndex) : false
  }

  const setStepCompleted = (recipeId: string, stepIndex: number, completed: boolean) => {
    checkRecipeProgressExpiration()
    if (!recipeProgress.value.completedSteps[recipeId]) {
      recipeProgress.value.completedSteps[recipeId] = []
    }
    const arr = recipeProgress.value.completedSteps[recipeId]
    const idx = arr.indexOf(stepIndex)
    if (completed && idx === -1) {
      arr.push(stepIndex)
    } else if (!completed && idx !== -1) {
      arr.splice(idx, 1)
    }
    saveRecipeProgress()
  }

  const getCompletedStepsCount = (recipeId: string): number => {
    checkRecipeProgressExpiration()
    return recipeProgress.value.completedSteps[recipeId]?.length || 0
  }

  const getCompletedStepsSet = (recipeId: string): Set<number> => {
    checkRecipeProgressExpiration()
    return new Set(recipeProgress.value.completedSteps[recipeId] || [])
  }

  const isIngredientChecked = (recipeId: string, articleId: string): boolean => {
    checkRecipeProgressExpiration()
    const ingList = recipeProgress.value.checkedIngredients[recipeId]
    return ingList ? ingList.includes(articleId) : false
  }

  const toggleIngredientChecked = (recipeId: string, articleId: string) => {
    checkRecipeProgressExpiration()
    if (!recipeProgress.value.checkedIngredients[recipeId]) {
      recipeProgress.value.checkedIngredients[recipeId] = []
    }
    const arr = recipeProgress.value.checkedIngredients[recipeId]
    const idx = arr.indexOf(articleId)
    if (idx === -1) {
      arr.push(articleId)
    } else {
      arr.splice(idx, 1)
    }
    saveRecipeProgress()
  }

  const resetRecipeProgress = (recipeId: string) => {
    checkRecipeProgressExpiration()
    const nextSteps = { ...recipeProgress.value.completedSteps }
    delete nextSteps[recipeId]
    const nextIngs = { ...recipeProgress.value.checkedIngredients }
    delete nextIngs[recipeId]
    recipeProgress.value = {
      ...recipeProgress.value,
      completedSteps: nextSteps,
      checkedIngredients: nextIngs
    }
    saveRecipeProgress()
  }

  const hasRecipeProgress = (recipeId: string): boolean => {
    checkRecipeProgressExpiration()
    const steps = recipeProgress.value.completedSteps[recipeId]
    const ings = recipeProgress.value.checkedIngredients[recipeId]
    return Boolean((steps && steps.length > 0) || (ings && ings.length > 0))
  }

  return {
    recipeProgress,
    initSync,
    stopSync,
    applyRemoteProgress,
    isStepCompleted,
    setStepCompleted,
    getCompletedStepsCount,
    getCompletedStepsSet,
    isIngredientChecked,
    toggleIngredientChecked,
    resetRecipeProgress,
    hasRecipeProgress
  }
})
